import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Archive, ArchiveRestore, Bell, ChevronDown, ExternalLink, MoreHorizontal, RotateCcw, XCircle } from 'lucide-react';
import type { SigningRequestSummary, SigningRequestStatus } from '@corner/shared';
import { APP } from '../tokens';
import { getActionError } from '../../../lib/actionErrorPresentation';

const F = APP.fontFamily;
const ARCHIVED_SIGNATURE_REQUESTS_KEY = 'corner:signatures:archived-request-ids';

/** Cohesive accents: neutral slate → signature blue → action teal → done emerald (no rainbow pastels). */
const TRACK = {
  inbox: {
    headerBg: 'rgba(51, 65, 85, 0.06)',
    headerBorder: 'rgba(51, 65, 85, 0.14)',
    count: '#334155',
  },
  signatures: {
    headerBg: 'rgba(37, 99, 235, 0.07)',
    headerBorder: 'rgba(37, 99, 235, 0.17)',
    count: '#1d4ed8',
  },
  attention: {
    headerBg: 'rgba(13, 148, 136, 0.07)',
    headerBorder: 'rgba(13, 148, 136, 0.17)',
    count: '#0f766e',
  },
  completed: {
    headerBg: 'rgba(5, 150, 105, 0.07)',
    headerBorder: 'rgba(5, 150, 105, 0.17)',
    count: '#047857',
  },
  archived: {
    headerBg: 'rgba(100, 116, 139, 0.07)',
    headerBorder: 'rgba(100, 116, 139, 0.16)',
    count: '#475569',
  },
} as const;

type TrackKey = keyof typeof TRACK;

function isRequestSuccessful(r: SigningRequestSummary): boolean {
  return r.totalCount > 0 && r.signedCount >= r.totalCount;
}

function statusLabel(status: SigningRequestStatus): string {
  switch (status) {
    case 'pending':
      return 'Awaiting signatures';
    case 'partially_signed':
      return 'In progress';
    case 'completed':
      return 'Completed';
    case 'revoked':
      return 'Revoked';
    case 'expired':
      return 'Expired';
    default:
      return status;
  }
}

function timeShort(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function readArchivedRequestIds(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(ARCHIVED_SIGNATURE_REQUESTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeArchivedRequestIds(ids: Set<string>): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(ARCHIVED_SIGNATURE_REQUESTS_KEY, JSON.stringify([...ids]));
}

export interface SignaturesDashboardProps {
  getToken?: () => string | null;
  refreshKey?: number;
  userDisplayName?: string;
  onOpenRequest: (r: SigningRequestSummary) => void;
  /** Opens contract-send wizard (recipients modal → full-screen doc → full-screen preview & send). */
  onSendForSignature: () => void | Promise<void>;
  contractSendBusy?: boolean;
  onOpenProjects?: () => void;
}

function TrackIcon({ variant }: { variant: TrackKey }) {
  const stroke = TRACK[variant].count;
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none' as const, strokeWidth: 1.75, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (variant) {
    case 'inbox':
      return (
        <svg {...common}>
          <path stroke={stroke} d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <path stroke={stroke} d="m22 6-10 7L2 6" />
        </svg>
      );
    case 'signatures':
      return (
        <svg {...common}>
          <path stroke={stroke} d="M22 2 11 13M22 2l-7 20-4-9-9-4 18-7z" />
        </svg>
      );
    case 'attention':
      return (
        <svg {...common}>
          <circle stroke={stroke} cx="12" cy="12" r="10" />
          <path stroke={stroke} d="M12 8v5M12 16h.01" />
        </svg>
      );
    case 'completed':
      return (
        <svg {...common}>
          <path stroke={stroke} d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <path stroke={stroke} d="M22 4 12 14.01l-3-3" />
        </svg>
      );
    case 'archived':
      return (
        <svg {...common}>
          <path stroke={stroke} d="M21 8v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8" />
          <path stroke={stroke} d="M1 3h22v5H1zM10 12h4" />
        </svg>
      );
    default:
      return null;
  }
}

function collapsedTrackBodyHeight(count: number): number {
  if (count >= 15) return 430;
  if (count >= 8) return 360;
  if (count >= 4) return 300;
  return 240;
}

/** Short BoldSign-style banner + expandable body height so dense columns can breathe. */
function TrackColumn({
  variant,
  title,
  count,
  emptyHint,
  children,
}: {
  variant: TrackKey;
  title: string;
  count: number;
  emptyHint: string;
  children: React.ReactNode;
}) {
  const t = TRACK[variant];
  const [expanded, setExpanded] = useState(false);
  const expandable = count > 5;
  const bodyMaxH = expanded ? undefined : collapsedTrackBodyHeight(count);
  const hiddenCount = Math.max(0, count - 5);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flex: '1 1 160px',
        maxWidth: '100%',
        borderRadius: 12,
        border: `1px solid ${APP.border}`,
        backgroundColor: APP.bgCardBody,
        overflow: 'hidden',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          minHeight: 48,
          boxSizing: 'border-box',
          backgroundColor: t.headerBg,
          borderBottom: `1px solid ${t.headerBorder}`,
        }}
      >
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.92 }}>
          <TrackIcon variant={variant} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: APP.textPrimary,
              fontFamily: F,
              lineHeight: 1.25,
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </span>
          <span
            style={{
              fontSize: 20,
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              color: t.count,
              fontFamily: F,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {count}
          </span>
        </div>
      </div>
      <div
        style={{
          position: 'relative',
          maxHeight: bodyMaxH,
          overflow: expanded ? 'visible' : 'hidden',
          padding: 8,
        }}
      >
        {children}
        {count === 0 ? (
          <p style={{ margin: 0, padding: '6px 4px', fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: 1.45 }}>{emptyHint}</p>
        ) : null}
        {expandable && !expanded ? (
          <div
            aria-hidden="true"
            style={{
              pointerEvents: 'none',
              position: 'sticky',
              bottom: -8,
              height: 28,
              margin: '-28px -8px 0',
              background: `linear-gradient(180deg, rgba(255,255,255,0), ${APP.bgCardBody})`,
            }}
          />
        ) : null}
      </div>
      {expandable ? (
        <button
          type="button"
          onClick={() => setExpanded((next) => !next)}
          aria-expanded={expanded}
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            width: '100%',
            padding: '7px 10px 8px',
            border: 'none',
            borderTop: `1px solid ${APP.border}`,
            backgroundColor: expanded ? APP.bgCardBody : t.headerBg,
            color: t.count,
            cursor: 'pointer',
            fontFamily: F,
            fontSize: 11,
            fontWeight: 650,
          }}
        >
          <span>{expanded ? 'Show less' : `Show more${hiddenCount > 0 ? ` (${hiddenCount})` : ''}`}</span>
          <ChevronDown
            aria-hidden="true"
            size={14}
            strokeWidth={2}
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 140ms ease',
            }}
          />
        </button>
      ) : null}
    </div>
  );
}

function SummaryStrip({
  total,
  inFlight,
  done,
  attn,
}: {
  total: number;
  inFlight: number;
  done: number;
  attn: number;
}) {
  const segments: Array<{ label: string; value: number; color?: string }> = [
    { label: 'Total', value: total },
    { label: 'In flight', value: inFlight, color: TRACK.signatures.count },
    { label: 'Completed', value: done, color: TRACK.completed.count },
    { label: 'Needs attention', value: attn, color: TRACK.attention.count },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: 0,
        marginTop: 12,
        borderRadius: 10,
        border: `1px solid ${APP.border}`,
        backgroundColor: APP.bgCardBody,
        overflow: 'hidden',
      }}
    >
      {segments.map((seg, i) => (
        <div
          key={seg.label}
          style={{
            flex: '1 1 72px',
            minWidth: 0,
            padding: '8px 12px',
            borderLeft: i === 0 ? 'none' : `1px solid ${APP.border}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: APP.textMuted, fontFamily: F }}>
            {seg.label}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: seg.color ?? APP.textPrimary, fontFamily: F, lineHeight: 1.2 }}>
            {seg.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function RequestRow({
  r,
  onOpen,
  archived = false,
  onArchive,
  onUnarchive,
  onSendReminder,
  onResendLink,
  onRevoke,
}: {
  r: SigningRequestSummary;
  onOpen: () => void;
  archived?: boolean;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onSendReminder?: () => void;
  onResendLink?: () => void;
  onRevoke?: () => void;
}) {
  const successful = isRequestSuccessful(r);
  const displayStatus: SigningRequestStatus = successful ? 'completed' : r.status;
  const [menuOpen, setMenuOpen] = useState(false);
  const canManageLiveRequest = r.status === 'pending' || r.status === 'partially_signed';
  const hasUnsignedSigner = r.signers.some((s) => s.status !== 'signed');

  const menuItems = [
    { label: 'Open', icon: ExternalLink, action: onOpen, tone: 'default' as const },
    ...(canManageLiveRequest && hasUnsignedSigner && !archived
      ? [
          { label: 'Send reminder', icon: Bell, action: onSendReminder, tone: 'default' as const },
          { label: 'Resend signing link', icon: RotateCcw, action: onResendLink, tone: 'default' as const },
        ]
      : []),
    ...(canManageLiveRequest && !archived
      ? [{ label: 'Revoke request', icon: XCircle, action: onRevoke, tone: 'danger' as const }]
      : []),
    archived
      ? { label: 'Restore from archive', icon: ArchiveRestore, action: onUnarchive, tone: 'default' as const }
      : { label: 'Archive', icon: Archive, action: onArchive, tone: 'default' as const },
  ].filter((item) => typeof item.action === 'function');

  return (
    <div
      onMouseLeave={() => setMenuOpen(false)}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
        textAlign: 'left',
        padding: '7px 6px 7px 8px',
        marginBottom: 5,
        borderRadius: 8,
        border: `1px solid ${APP.border}`,
        backgroundColor: APP.bgQuickAction,
        cursor: 'pointer',
        fontFamily: F,
      }}
    >
      <button
        type="button"
        onClick={onOpen}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 2,
          padding: 0,
          border: 'none',
          background: 'transparent',
          textAlign: 'left',
          cursor: 'pointer',
          fontFamily: F,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: APP.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.documentName}>
          {r.documentName}
        </span>
        <span style={{ fontSize: 10, color: APP.textMuted }}>
          {statusLabel(displayStatus)}
          {' · '}
          {r.signedCount}/{r.totalCount} signed
        </span>
        <span style={{ fontSize: 10, color: APP.textMuted }}>{timeShort(r.completedAt ?? r.createdAt)}</span>
      </button>
      <button
        type="button"
        aria-label={`Actions for ${r.documentName}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        onClick={(event) => {
          event.stopPropagation();
          setMenuOpen((next) => !next);
        }}
        style={{
          flexShrink: 0,
          width: 26,
          height: 26,
          borderRadius: 7,
          border: `1px solid ${menuOpen ? APP.border : 'transparent'}`,
          backgroundColor: menuOpen ? APP.bgCardBody : 'transparent',
          color: APP.textMuted,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: menuOpen ? 1 : 0.72,
        }}
      >
        <MoreHorizontal size={16} strokeWidth={2} />
      </button>
      {menuOpen ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            zIndex: 30,
            top: 34,
            right: 6,
            width: 188,
            padding: 5,
            borderRadius: 9,
            border: `1px solid ${APP.border}`,
            backgroundColor: APP.bgCardBody,
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.14)',
          }}
        >
          {menuItems.map((item) => {
            const Icon = item.icon;
            const danger = item.tone === 'danger';
            return (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                onClick={(event) => {
                  event.stopPropagation();
                  setMenuOpen(false);
                  item.action?.();
                }}
                style={{
                  width: '100%',
                  border: 'none',
                  borderRadius: 7,
                  padding: '7px 8px',
                  background: 'transparent',
                  color: danger ? '#B42318' : APP.textPrimary,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontFamily: F,
                  fontSize: 12,
                  fontWeight: 550,
                  textAlign: 'left',
                }}
              >
                <Icon size={14} strokeWidth={1.9} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default function SignaturesDashboard({
  getToken,
  refreshKey = 0,
  userDisplayName,
  onOpenRequest,
  onSendForSignature,
  contractSendBusy = false,
  onOpenProjects,
}: SignaturesDashboardProps) {
  const [rows, setRows] = useState<SigningRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archivedRequestIds, setArchivedRequestIds] = useState<Set<string>>(() => readArchivedRequestIds());

  const load = useCallback(
    (opts?: { silent?: boolean }) => {
      const silent = opts?.silent ?? false;
      const token = getToken?.();
      if (!token) {
        setRows([]);
        setLoading(false);
        return;
      }
      if (!silent) setLoading(true);
      axios
        .get<SigningRequestSummary[]>('/api/signing', { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          const list = Array.isArray(res.data) ? res.data : [];
          setRows(list);
          setError(null);
        })
        .catch((err: unknown) => {
          if (!silent) {
            setError(err instanceof Error ? err.message : 'Could not load signature requests');
            setRows([]);
          }
        })
        .finally(() => {
          if (!silent) setLoading(false);
        });
    },
    [getToken],
  );

  const archiveRequest = useCallback((requestId: string) => {
    setArchivedRequestIds((current) => {
      const next = new Set(current);
      next.add(requestId);
      writeArchivedRequestIds(next);
      return next;
    });
  }, []);

  const unarchiveRequest = useCallback((requestId: string) => {
    setArchivedRequestIds((current) => {
      const next = new Set(current);
      next.delete(requestId);
      writeArchivedRequestIds(next);
      return next;
    });
  }, []);

  const unsignedSignerEmail = useCallback((request: SigningRequestSummary): string | null => {
    return request.signers.find((signer) => signer.status !== 'signed')?.email ?? null;
  }, []);

  const postSigningAction = useCallback(
    async (path: string, failureMessage: string) => {
      const token = getToken?.();
      if (!token) {
        setError('Please sign in again to manage signature requests.');
        return;
      }
      try {
        await axios.post(path, {}, { headers: { Authorization: `Bearer ${token}` } });
        setError(null);
        load({ silent: true });
      } catch (err) {
        const e = getActionError(failureMessage, err);
        setError(e.description);
      }
    },
    [getToken, load],
  );

  const sendReminder = useCallback(
    (request: SigningRequestSummary) => {
      const email = unsignedSignerEmail(request);
      if (!email) return;
      void postSigningAction(
        `/api/signing/${encodeURIComponent(request.requestId)}/reminder/${encodeURIComponent(email)}`,
        'Could not send reminder.',
      );
    },
    [postSigningAction, unsignedSignerEmail],
  );

  const resendLink = useCallback(
    (request: SigningRequestSummary) => {
      const email = unsignedSignerEmail(request);
      if (!email) return;
      void postSigningAction(
        `/api/signing/${encodeURIComponent(request.requestId)}/resend/${encodeURIComponent(email)}`,
        'Could not resend signing link.',
      );
    },
    [postSigningAction, unsignedSignerEmail],
  );

  const revokeRequest = useCallback(
    (request: SigningRequestSummary) => {
      const ok = window.confirm(`Revoke "${request.documentName}"? Unsigned recipients will no longer be able to sign.`);
      if (!ok) return;
      void postSigningAction(
        `/api/signing/${encodeURIComponent(request.requestId)}/revoke`,
        'Could not revoke request.',
      );
    },
    [postSigningAction],
  );

  useEffect(() => {
    const t = window.setTimeout(() => load(), 0);
    return () => window.clearTimeout(t);
  }, [load, refreshKey]);

  useEffect(() => {
    const token = getToken?.();
    if (!token) return;
    const hasInFlight = rows.some((r) => r.status === 'pending' || r.status === 'partially_signed');
    if (!hasInFlight) return;
    const id = window.setInterval(() => load({ silent: true }), 15000);
    return () => window.clearInterval(id);
  }, [rows, getToken, load]);

  const firstName = userDisplayName?.split(' ')[0] ?? '';

  const { awaitingOthers, needsAttention, completed, archived, stats } = useMemo(() => {
    const awaitingOthers: SigningRequestSummary[] = [];
    const needsAttention: SigningRequestSummary[] = [];
    const completed: SigningRequestSummary[] = [];
    const archived: SigningRequestSummary[] = [];
    for (const r of rows) {
      if (archivedRequestIds.has(r.requestId)) {
        archived.push(r);
        continue;
      }
      if (r.status === 'revoked' || r.status === 'expired') {
        needsAttention.push(r);
        continue;
      }
      if (isRequestSuccessful(r) || r.status === 'completed') {
        completed.push(r);
        continue;
      }
      if (r.status === 'pending' || r.status === 'partially_signed') {
        awaitingOthers.push(r);
      }
    }
    const inFlight = awaitingOthers.length;
    const done = completed.length;
    const attn = needsAttention.length;
    return { awaitingOthers, needsAttention, completed, archived, stats: { total: rows.length, inFlight, done, attn } };
  }, [archivedRequestIds, rows]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 0%',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: APP.bgDocColumn,
      }}
    >
      <div style={{ flexShrink: 0, padding: '16px 20px 12px', borderBottom: `1px solid ${APP.border}`, backgroundColor: APP.bgContent }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: APP.textPrimary, fontFamily: F, letterSpacing: '-0.02em' }}>
              {firstName ? `Hello ${firstName}` : 'Signatures'}
            </h1>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: APP.textMuted, fontFamily: F, lineHeight: 1.45, maxWidth: 560 }}>
              Status of envelopes you send from Corner. Open a row to work the document or track signer progress.
            </p>
            {onOpenProjects ? (
              <p style={{ margin: '8px 0 0', fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: 1.4 }}>
                Shared folders &amp; matter-style organization →{' '}
                <button
                  type="button"
                  onClick={onOpenProjects}
                  style={{
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontFamily: F,
                    fontSize: 11,
                    fontWeight: 600,
                    color: TRACK.signatures.count,
                    textDecoration: 'underline',
                    textUnderlineOffset: 2,
                  }}
                >
                  Projects
                </button>
              </p>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => void onSendForSignature()}
              disabled={contractSendBusy}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: 'none',
                backgroundColor: APP.bgExportChipActive,
                fontFamily: F,
                fontSize: 12,
                fontWeight: 600,
                color: '#FFFFFF',
                cursor: contractSendBusy ? 'not-allowed' : 'pointer',
                opacity: contractSendBusy ? 0.7 : 1,
              }}
            >
              {contractSendBusy ? 'Starting…' : 'Send for signature'}
            </button>
          </div>
        </div>

        <SummaryStrip total={stats.total} inFlight={stats.inFlight} done={stats.done} attn={stats.attn} />
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '12px 16px 16px' }}>
        {loading && <p style={{ fontFamily: F, fontSize: 13, color: APP.textMuted }}>Loading…</p>}
        {error && !loading && <p style={{ fontFamily: F, fontSize: 13, color: '#c44' }}>{error}</p>}
        {!loading && !error && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
              gap: 10,
              alignItems: 'start',
            }}
          >
            <TrackColumn
              variant="inbox"
              title="Waiting on you"
              count={0}
              emptyHint="Nothing here yet. When someone sends you a Corner envelope to sign, it will appear in this column."
            >
              <></>
            </TrackColumn>

            <TrackColumn variant="signatures" title="Waiting on signatures" count={awaitingOthers.length} emptyHint="No envelopes out yet. Use Send for signature above to pick a document and invite signers.">
              {awaitingOthers.map((r) => (
                <RequestRow
                  key={r.requestId}
                  r={r}
                  onOpen={() => onOpenRequest(r)}
                  onArchive={() => archiveRequest(r.requestId)}
                  onSendReminder={() => sendReminder(r)}
                  onResendLink={() => resendLink(r)}
                  onRevoke={() => revokeRequest(r)}
                />
              ))}
            </TrackColumn>

            <TrackColumn variant="attention" title="Needs attention" count={needsAttention.length} emptyHint="No revoked or expired envelopes.">
              {needsAttention.map((r) => (
                <RequestRow
                  key={r.requestId}
                  r={r}
                  onOpen={() => onOpenRequest(r)}
                  onArchive={() => archiveRequest(r.requestId)}
                />
              ))}
            </TrackColumn>

            <TrackColumn variant="completed" title="Completed" count={completed.length} emptyHint="No completed envelopes yet.">
              {completed.map((r) => (
                <RequestRow
                  key={r.requestId}
                  r={r}
                  onOpen={() => onOpenRequest(r)}
                  onArchive={() => archiveRequest(r.requestId)}
                />
              ))}
            </TrackColumn>

            <TrackColumn variant="archived" title="Archived" count={archived.length} emptyHint="Archived signature requests will appear here.">
              {archived.map((r) => (
                <RequestRow
                  key={r.requestId}
                  r={r}
                  archived
                  onOpen={() => onOpenRequest(r)}
                  onUnarchive={() => unarchiveRequest(r.requestId)}
                />
              ))}
            </TrackColumn>
          </div>
        )}
      </div>
    </div>
  );
}
